import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Switch, Select
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    PlusOutlined, EditOutlined, HomeOutlined, BuildOutlined,
    SearchOutlined, CheckCircleFilled, CloseCircleFilled
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import Highlighter from "react-highlight-words";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;

const ManageFields = () => {
    const [fields, setFields] = useState([]);
    const [lands, setLands] = useState([]);
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
                    <Button type="link" size="small" onClick={() => { confirm({ closeDropdown: false }); setSearchText(selectedKeys[0]); setSearchedColumn(dataIndex); }}>Filter</Button>
                    <Button type="link" size="small" onClick={() => close()}>close</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: { onOpenChange(open) { if (open) setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]} autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        ) : text,
    });

    const fetchActiveLands = async () => {
        try {
            const res = await api.get('/masterdropdown/listof_activelands.php');
            setLands(res.data.dataset);
        } catch {
            message.error("Failed to load lands.");
        }
    };

    const fetchSectionsByLand = async (landId) => {
        try {
            const res = await api.get(`/masterdropdown/listof_activesections.php?land_id=${landId}`);
            setSections(res.data.dataset);
        } catch {
            message.error("Failed to load sections.");
        }
    };

    const fetchFields = useCallback(async (page = 1, pageSize = 10) => {
        try {
            setLoading(true);
            const res = await api.get(`/farmSetUp/fields/list.php?page=${page}&limit=${pageSize}`);
            setFields(res.data.dataset.map(item => ({
                fieldId: item.field_id,
                landId: item.land_id,
                landName: item.land_name,
                sectionId: item.section_id,
                sectionName: item.section_name,
                fieldCode: item.field_code,
                fieldActive: item.field_active,
            })));
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch fields.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFields(1, 10);
        fetchActiveLands();
    }, [fetchFields]);

    const handleTableChange = (pg) => fetchFields(pg.current, pg.pageSize);

    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/farmSetUp/fields/add.php', {
                land_id: values.landId,
                section_id: values.sectionId,
                field_code: values.fieldCode,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Field added successfully.");
                setIsAddModalVisible(false);
                fetchFields(pagination.current, pagination.pageSize);
                addForm.resetFields();
                setSections([]);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to add field.");
        } finally { setLoading(false); }
    };

    const openEdit = (record) => {
        setCurrentItem(record);
        fetchSectionsByLand(record.landId);
        editForm.setFieldsValue({
            landId: record.landId,
            sectionId: record.sectionId,
            fieldCode: record.fieldCode,
        });
        setIsModalVisible(true);
    };

    const handleEdit = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/farmSetUp/fields/edit.php', {
                field_id: currentItem.fieldId,
                land_id: values.landId,
                section_id: values.sectionId,
                field_code: values.fieldCode,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Field updated successfully.");
                setIsModalVisible(false);
                fetchFields(pagination.current, pagination.pageSize);
                editForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update field.");
        } finally { setLoading(false); }
    };

    const handleToggle = async (record, checked) => {
        try {
            const res = await api.post('/farmSetUp/fields/toggle.php', {
                field_id: record.fieldId,
                field_active: checked ? 1 : 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                fetchFields(pagination.current, pagination.pageSize);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update status.");
        }
    };

    const columns = [
        {
            title: 'Land', dataIndex: 'landName', key: 'landName',
            ...getColumnSearchProps('landName'),
        },
        {
            title: 'Section', dataIndex: 'sectionName', key: 'sectionName',
            ...getColumnSearchProps('sectionName'),
        },
        {
            title: 'Field Code', dataIndex: 'fieldCode', key: 'fieldCode',
            ...getColumnSearchProps('fieldCode'),
        },
        {
            title: 'Status', dataIndex: 'fieldActive', key: 'fieldActive',
            align: 'center', width: 100,
            render: (val) => val == 1
                ? <Tooltip title="Active"><Space size={4}><CheckCircleFilled style={{ color: 'green', fontSize: 16 }} /><Typography.Text style={{ color: 'green' }}>Active</Typography.Text></Space></Tooltip>
                : <Tooltip title="Inactive"><Space size={4}><CloseCircleFilled style={{ color: 'red', fontSize: 16 }} /><Typography.Text style={{ color: 'red' }}>Inactive</Typography.Text></Space></Tooltip>,
        },
        {
            title: 'Action', key: 'action', align: 'center', fixed: 'right', width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit Field">
                        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    </Tooltip>
                    <Tooltip title={record.fieldActive == 1 ? "Deactivate" : "Activate"}>
                        <Switch
                            size="small"
                            checked={record.fieldActive == 1}
                            onChange={(checked) => handleToggle(record, checked)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                                                    <Breadcrumb
                                className="fluid-breadcrumb"
                                items={[
                                    { href: '/dash', title: <HomeOutlined /> },
                                    { href: '/farmoverview', title: 'Farm Overview' },
                                    { title: 'Manage Fields' },
                                ]}
                            />
                        <h1 className="fluid-page-title">
                            <BuildOutlined /> Manage Fields
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Define specific cultivation fields within farm sections</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-card-header">
                        <h3 className="fluid-card-title">Field List</h3>
                        <Space>
                            <Typography.Text type="secondary">{pagination.total} records</Typography.Text>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddModalVisible(true)}>
                                Add New Field
                            </Button>
                        </Space>
                    </div>
                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={fields}
                            rowKey="fieldId"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 800 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            <Modal
                title="Add New Field"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); setSections([]); }}
                footer={null}
                width={600}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Land" name="landId" rules={[{ required: true, message: 'Please select land' }]}>
                                <Select allowClear placeholder="Select Land" onChange={(val) => { addForm.setFieldsValue({ sectionId: undefined }); fetchSectionsByLand(val); }}>
                                    {lands.map(l => <Option key={l.value} value={l.value}>{l.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Section" name="sectionId" rules={[{ required: true, message: 'Please select section' }]}>
                                <Select allowClear placeholder="Select Section" disabled={!sections.length}>
                                    {sections.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Field Code" name="fieldCode" rules={[{ required: true, message: 'Field code required' }]}>
                                <Input placeholder="F-01" maxLength={20} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); setSections([]); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Add Field</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title="Edit Field Details"
                open={isModalVisible}
                onCancel={() => { setIsModalVisible(false); editForm.resetFields(); }}
                footer={null}
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    <Form.Item label="Land" name="landId" rules={[{ required: true }]}>
                        <Select allowClear placeholder="Select Land" onChange={(val) => { editForm.setFieldsValue({ sectionId: undefined }); fetchSectionsByLand(val); }}>
                            {lands.map(l => <Option key={l.value} value={l.value}>{l.label}</Option>)}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Section" name="sectionId" rules={[{ required: true }]}>
                        <Select allowClear placeholder="Select Section">
                            {sections.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Field Code" name="fieldCode" rules={[{ required: true }]}>
                        <Input maxLength={20} />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                            <Button danger onClick={() => setIsModalVisible(false)}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ManageFields;
